<template>
  <div class="create-board-game-page container">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1 class="text-center m-0">
        {{ isEditing ? 'Edit Board Game' : 'Create a New Board Game' }}
      </h1>
      <button 
        type="button" 
        class="btn-close" 
        @click="$emit('close')"
        aria-label="Close"
      ></button>
    </div>
    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <form @submit.prevent="createBoardGame" class="board-game-form">
      <div class="form-group mb-3">
        <label for="name" class="form-label">Board Game Name</label>
        <input 
          type="text" 
          id="name" 
          class="form-control" 
          v-model="boardGame.name" 
          required 
          placeholder="Enter board game name"
        />
      </div>

      <div class="form-group mb-3">
        <label for="description" class="form-label">Description</label>
        <textarea 
          id="description" 
          class="form-control" 
          v-model="boardGame.description" 
          required 
          placeholder="Describe your board game"
          rows="4"
        ></textarea>
      </div>

      <div class="form-group mb-3">
        <label for="type" class="form-label">Game Type</label>
        <select 
          id="type" 
          class="form-select" 
          v-model="boardGame.type" 
          required
        >
          <option value="">Select Game Type</option>
          <option value="strategy">Strategy</option>
          <option value="party">Party</option>
          <option value="cooperative">Cooperative</option>
          <option value="card">Card</option>
          <option value="board">Board</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div class="form-group mb-3">
        <label for="players" class="form-label">Number of Players</label>
        <input 
          type="number" 
          id="players" 
          class="form-control" 
          v-model.number="boardGame.minPlayers" 
          min="1" 
          max="20" 
          placeholder="Minimum number of players"
          required
        />
      </div>

      <div class="form-group mb-3">
        <label for="complexity" class="form-label">Complexity Level</label>
        <select 
          id="complexity" 
          class="form-select" 
          v-model="boardGame.complexity" 
          required
        >
          <option value="">Select Complexity</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
          <option value="expert">Expert</option>
        </select>
      </div>

      <div class="form-group mb-3">
        <label for="cardStyle" class="form-label">Card Style</label>
        <textarea 
          id="cardStyle" 
          class="form-control" 
          v-model="boardGame.cardStyle" 
          placeholder="Describe the visual style for the game's cards (e.g., 'fantasy medieval style with ornate borders')"
          rows="3"
        ></textarea>
      </div>

      <button 
        type="submit" 
        class="btn btn-primary w-100" 
        :disabled="isSubmitting"
      >
        {{ isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Board Game' : 'Create Board Game') }}
      </button>
    </form>
  </div>
</template>

<script>
import { getAuth } from 'firebase/auth'
import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore'
import { firestore } from '@/firebase'

export default {
  name: 'CreateBoardGamePage',
  props: {
    boardGameId: {
      type: String,
      default: null
    },
    isEditing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      boardGame: {
        name: '',
        description: '',
        type: '',
        minPlayers: 1,
        complexity: '',
        cardStyle: '',
      },
      error: null,
      isSubmitting: false
    }
  },
  created() {
    // this.isEditing = !!this.boardGameId
    // console.log(this.isEditing)
  },
  methods: {
    async createBoardGame() {
      // Reset error
      this.error = null
      
      // Validate form
      if (!this.boardGame.name || 
          !this.boardGame.description || 
          !this.boardGame.type ||
          !this.boardGame.complexity) {
        this.error = 'Please fill out all fields'
        return
      }

      // Get current user
      const auth = getAuth()
      const user = auth.currentUser

      if (!user) {
        this.error = 'You must be logged in to create a board game'
        return
      }

      try {
        // Set submitting state
        this.isSubmitting = true

        // Prepare board game data
        const boardGameData = {
          ...this.boardGame,
          userId: user.uid,
          updatedAt: Timestamp.now()
        }

        if (this.isEditing) {
          console.log( this.boardGameId)
          // Update existing board game
          const boardGameRef = doc(firestore, 'boardGames', this.boardGameId)
          await updateDoc(boardGameRef, boardGameData)

          // Emit an event to notify parent component
          this.$emit('board-game-updated', {
            id: this.boardGameId,
            ...boardGameData
          })

          // Close the modal
          this.$emit('close')
        } else {
          // Create new board game
          boardGameData.createdAt = Timestamp.now()
          boardGameData.approved = false
          const boardGamesRef = collection(firestore, 'boardGames')
          const newBoardGameRef = await addDoc(boardGamesRef, boardGameData)

          // Navigate to the newly created board game's detail page
          this.$router.push({
            name: 'BoardGameDetail', 
            params: { id: newBoardGameRef.id },
            query: { 
              message: `Board game "${this.boardGame.name}" created successfully!`
            }
          })
        }
      } catch (err) {
        // Handle errors
        console.error('Error creating/updating board game:', err)
        this.error = err.message || 'Failed to create/update board game. Please try again.'
      } finally {
        // Reset submitting state
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.create-board-game-page {
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
}
</style>